/**
 * src/ui/vinculo.ts — a tela de VÍNCULO por QR (a TV esperando aprovação).
 *
 * Substitui o pareamento por código de 6 dígitos (que o web não usa mais). O que
 * aparece na TV agora (TV-QR-APPROVAL §3/§5):
 *   - o QR com o deep link `simpleindor://link?r=<requestId>`;
 *   - o CÓDIGO DE CONFERÊNCIA de 4 caracteres, gigante — o lojista compara com o
 *     que aparece no celular antes de aprovar (quebra o phishing);
 *   - a URL de fallback em texto, para celular sem câmera que resolve scheme.
 *
 * O `requestSecret` (o segredo real do dispositivo) NUNCA aparece aqui: só o
 * `requestId` vai no QR, e ele não concede nada sozinho.
 *
 * Dependência: `qrcode` (npm, JS puro), a mesma do `pareamento.ts` removido —
 * desenhada em `<canvas>`, que não carrega fonte externa nem vaza texto ao DOM.
 */
import QRCode from 'qrcode';
import type { LinkDisplay } from '../../net';

export interface VinculoView {
  setPayload(payload: string): Promise<void>;
  setCode(code: string | null): void;
  setOffline(offline: boolean): void;
}

function el(tag: string, className?: string, text?: string): HTMLElement {
  const node = document.createElement(tag);
  if (className) {
    node.className = className;
  }
  if (text !== undefined) {
    node.textContent = text;
  }
  return node;
}

/** A tela completa de vínculo, montada dentro do contêiner de estado. */
export function renderVinculo(options: { display?: LinkDisplay; offline?: boolean } = {}): HTMLElement {
  const root = el('section', 'tela tela--vinculo');
  root.setAttribute('role', 'status');
  root.setAttribute('aria-live', 'polite');
  root.dataset['estado'] = 'vinculo';

  root.append(el('h1', 'tela__titulo', 'Vincular esta tela'));

  const qrBox = el('div', 'qr');
  qrBox.dataset['papel'] = 'qr';
  qrBox.append(el('canvas', 'qr__canvas'));
  root.append(qrBox);

  // O código de CONFERÊNCIA — não é segredo e não autoriza nada: serve para o
  // lojista ter certeza de que a TV à sua frente é a que o celular aprovou.
  const code = el('p', 'codigo');
  code.dataset['papel'] = 'codigo';
  code.textContent = options.display?.confirmationCode ?? '····';
  code.setAttribute('aria-label', 'Código de conferência');
  root.append(code);

  root.append(
    el(
      'p',
      'instrucao',
      'No celular, abra o app Câmera e aponte para este QR. O Simple Indoor abre sozinho para você conferir o código e aprovar.',
    ),
  );

  const url = el('p', 'vinculo__url');
  url.dataset['papel'] = 'url-fallback';
  url.textContent = options.display?.fallbackUrl ?? '';
  url.hidden = !options.display?.fallbackUrl;
  root.append(url);

  if (options.offline) {
    root.append(el('p', 'faixa faixa--aviso', 'Sem internet'));
  }
  return root;
}

/** Prepara o canvas do QR e a URL dentro de um nó de `renderVinculo`. */
export function mountVinculo(container: HTMLElement): VinculoView {
  const canvas = container.querySelector<HTMLCanvasElement>('canvas.qr__canvas');
  const codeEl = container.querySelector<HTMLElement>('[data-papel="codigo"]');
  const urlEl = container.querySelector<HTMLElement>('[data-papel="url-fallback"]');
  if (!canvas || !codeEl) {
    throw new Error('vínculo: canvas.qr__canvas e [data-papel=codigo] são obrigatórios');
  }
  return {
    setPayload: async payload => {
      await QRCode.toCanvas(canvas, payload, { margin: 1, width: 320 });
    },
    setCode: code => {
      codeEl.textContent = code ?? '····';
      codeEl.dataset['preenchido'] = code ? 'sim' : 'nao';
    },
    setOffline: offline => {
      if (!urlEl) return;
      urlEl.classList.toggle('vinculo__url--offline', offline);
    },
  };
}

/**
 * "O pedido expirou" (5 min sem aprovação) — a tela EXPLICA e oferece pedir
 * outro. Não é renovação silenciosa: o lojista precisa ver que o QR mudou.
 */
export function renderVinculoExpirado(): HTMLElement {
  const root = el('section', 'tela tela--vinculo');
  root.dataset['estado'] = 'vinculo-expirado';
  root.setAttribute('role', 'status');
  root.append(el('h1', 'tela__titulo', 'O pedido expirou'));
  root.append(
    el('p', 'tela__mensagem', 'Ninguém aprovou esta tela a tempo. Toque em gerar um novo código.'),
  );
  return root;
}

/** "O celular recusou este vínculo" — o mesmo: explica, não repete sozinho. */
export function renderVinculoRecusado(): HTMLElement {
  const root = el('section', 'tela tela--vinculo');
  root.dataset['estado'] = 'vinculo-recusado';
  root.setAttribute('role', 'status');
  root.append(el('h1', 'tela__titulo', 'Vínculo recusado'));
  root.append(
    el('p', 'tela__mensagem', 'O pedido foi recusado no celular. Toque em gerar um novo código.'),
  );
  return root;
}
